import { useEffect } from 'react';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';

import Google from '~/assets/img/social/google.svg';
import { Logo } from '~/components/modules/Logo/Logo';
import { RegisterTrackingKey } from '~/components/views/Signup/keys';
import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { PosthogHelper } from '~/helpers/PosthogHelper';
import { LocalStorageHelper } from '~/helpers/StorageHelper';
import { useCaptureUtmParams } from '~/hooks/tracking/useCaptureUtmParams';
import { useTracking } from '~/hooks/tracking/useTracking';
import { useWindowSize } from '~/hooks/useWindowSize';
import { baseRoutes } from '~/routes/applicationRoutes';
import { ReferralCookieHelper } from '~/tracking/referral/ReferralCookieHelper';
import { Button, Heading, Text } from '~/vinovest-design-system/components';
import { InlineLink } from '~/vinovest-design-system/components/Button';

import { CreateAccountForm } from './CreateAccountForm';
import { SocialLoginHelper } from '../Login/SocialLoginHelper';
import type { StaticImport } from 'next/dist/shared/lib/get-img-props';

export const SignupPage = () => {
    useCaptureUtmParams();
    const { captureEvent } = useTracking();

    const { isMobile } = useWindowSize();

    const router = useRouter();

    const googleSignUp = () => {
        LocalStorageHelper.setItem(RegisterTrackingKey, AnalyticsEventNames.GoogleSignUpClick);
        LocalStorageHelper.setItem('is_new_user', true);
        SocialLoginHelper.onGoogleSignIn();
        captureEvent(AnalyticsEventNames.GoogleSignUpClick);
    };

    useEffect(() => {
        const growSurfReferralCode = router.query.grsf;
        if (growSurfReferralCode) {
            ReferralCookieHelper.setReferralCookie(growSurfReferralCode as string);
        }
    }, [router]);

    return (
        <>
            <div className={'flex lg:flex-row flex-col h-screen'}>
                <div className={'flex-col flex-1'}>
                    <div className={'mx-auto px-4 sm:px-6 lg:px-12 flex items-center'}>
                        <div className={'xs:mx-auto md:mx-0'}>
                            <div className={'lg:pr-5 xs:mx-auto md:mx-0'}>
                                <Logo
                                    type={'standard'}
                                    className={'h-[60px] w-[60px] !md:w-[90px] !md:h-[90px]'}
                                    width={isMobile ? '60' : '90'}
                                    height={isMobile ? '60' : '90'}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={'flex justify-between flex-col md:flex-row'}>
                        <div
                            className={
                                'flex px-10 flex-col sm:flex-1 justify-center mx-auto md:mx-8 md:w-[50%] self-start md:mt-16 mt-8 mb-4'
                            }
                        >
                            <Heading level={'h1'} type={'h2'} classnames={'text-center xs:pt-8 xs:pb-8 lg:pb-12'}>
                                Start Investing Today!
                            </Heading>
                            <Button
                                variant={'secondary'}
                                classnames={'my-4 relative min-w-full sm:min-w-[25rem] mx-auto'}
                                onClick={googleSignUp}
                            >
                                <div className={'absolute left-4 top-[calc(50%-10px)]'}>
                                    <Image height={'20'} width={'20'} src={Google as StaticImport} />
                                </div>
                                <span className={'text-14 px-2'}>SIGN UP WITH GOOGLE</span>
                            </Button>
                            <div
                                className={
                                    'vinovest-mono text-16 align-center flex justify-center bg-gray-400 h-[1px] mt-8 mb-10 w-full max-w-[25rem] mx-auto'
                                }
                            >
                                <span className={'text-gray-500 bg-white px-8 inline-block -mt-[10px]'}>OR</span>
                            </div>
                            <CreateAccountForm />
                            <Text
                                classnames={
                                    'text-center text-16 font-vinovestmedium mt-4 min-w-full sm:min-w-[25rem] mx-auto'
                                }
                            >
                                Already have an account?{' '}
                                <InlineLink
                                    onClick={(e) => {
                                        e.preventDefault();
                                        PosthogHelper.captureEvent(AnalyticsEventNames.UserSelectLogin);
                                        router.push(baseRoutes.login.href);
                                    }}
                                >
                                    log in
                                </InlineLink>
                            </Text>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
